// The React app root.
//
// Through Phase 3 this rendered nothing — the legacy vanilla-JS app owned the
// whole page and React only mounted widget bodies via the `window.bentoReact`
// bridge. Phase 4 (PR 9) is the shell cutover: React now renders the desktop
// module canvas from the store (`<Workspace>` portals each `<PlacedModule>` into
// the legacy `#workspace`). The rest of the page — top bar, side panel, mobile
// tray, tour — is still legacy DOM and is ported in later PRs.
//
// WS-D D2: wrapped in <AuthProvider>. On a real (Cognito-configured) build the
// dashboard is gated behind <AuthOverlay> until sign-in; unconfigured builds
// (local dev, CI/VRT) skip the gate entirely and render the dashboard exactly as
// before — so the committed VRT baselines and behavioural e2e are untouched.
import { AuthProvider, useAuth } from "./contexts/AuthContext.js";
import { AMPLIFY_CONFIGURED } from "./lib/amplify.js";
import AuthOverlay from "./components/auth/AuthOverlay.js";
import AuthBridge from "./auth-bridge.js";
import BoardsBridge from "./boards-bridge.js";
import Workspace from "./components/Workspace.js";
import FixturesSync from "./data/FixturesSync.js";
import PinExpiry from "./data/PinExpiry.js";

function Dashboard() {
  return (
    <>
      {/* Feeds the real fixture season into the legacy selector (WS-C). Renders
          nothing; just bridges the app-api fetch to main.js's <select>. */}
      <FixturesSync />
      <PinExpiry />
      <Workspace />
    </>
  );
}

function Gate() {
  const { user, loading } = useAuth();

  // Unconfigured build → no auth, dashboard as before (see file header).
  if (!AMPLIFY_CONFIGURED) return <Dashboard />;

  // Configured but still resolving the session: hold the gate closed so the
  // dashboard never flashes before we know whether the user is signed in.
  if (loading) return <div className="auth-overlay auth-overlay-loading" aria-busy="true" />;

  return user ? <Dashboard /> : <AuthOverlay />;
}

export default function App() {
  return (
    <AuthProvider>
      <AuthBridge />
      {/* Publishes window.bentoBoards + auto-loads the saved board on sign-in.
          Self-gates on `user`, so it's inert on unconfigured/signed-out builds. */}
      <BoardsBridge />
      <Gate />
    </AuthProvider>
  );
}
